import React, { useMemo, useCallback } from 'react';
import IndeterminateCheckbox from '../IndeterminateCheckbox';
import { useSelectedChannels } from '../../contexts/SelectedChannelsContext';

// Simple chevron
function Chevron({ open }) {
  return (
    <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms' }}>
        { '\u25B6' }
    </span>
  );
}

export default function ChannelTreeNode({
  node,
  depth,
  expandedPaths,
  setExpandedPaths,
  disabled,
}) {
  const { selectedIds, toggleOne, selectMany, deselectMany } = useSelectedChannels();

  const state = useMemo(() => {
    const ids = node.leafIds;
    let selectedCount = 0;
    ids.forEach(id => { if (selectedIds.has(id)) selectedCount += 1; });

    let state = 'unchecked';
    if (selectedCount === 0) state = 'unchecked';
    else if (selectedCount === ids.size) state = 'checked';
    else state = 'mixed';

    return state;
  }, [node.leafIds, selectedIds]);

  const isExpandable = node.children.length > 0;
  const isExpanded = expandedPaths.has(node.path);

  const toggleExpand = useCallback(() => {
    if (!isExpandable) return;
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(node.path)) next.delete(node.path);
      else next.add(node.path);
      return next;
    });
  }, [isExpandable, setExpandedPaths, node.path]);

  const onToggleCheckbox = useCallback(() => {
    if (node.channel) {
      toggleOne(node.channel.id);
      return;
    }
    // parent node: select/deselect all leaves under it
    const ids = Array.from(node.leafIds);
    if (state === 'checked') {
      deselectMany(ids);
    } else {
      selectMany(ids);
    }
  }, [node, toggleOne, selectMany, deselectMany, state]);

    return (
      <div style={{ paddingLeft: depth * 12 }}>
        {/* header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={toggleExpand}
            aria-label={isExpandable ? (isExpanded ? 'Collapse' : 'Expand') : 'Leaf'}
            title={isExpandable ? (isExpanded ? 'Collapse' : 'Expand') : ''}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: isExpandable ? 'pointer' : 'default',
              color: 'inherit',
              width: 18,
            }}
          >
            {isExpandable ? <Chevron open={isExpanded} /> : <span style={{ display: 'inline-block', width: 10 }} />}
          </button>

          <IndeterminateCheckbox
            checked={state === 'checked'}
            indeterminate={state === 'mixed'}
            onChange={onToggleCheckbox}
            disabled={disabled}
          />

          <span title={node.channel?.fullname || node.path} style={{ fontFamily: 'monospace' }}>
            {node.name}
          </span>

          {node.channel && (
            <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>
              (id: {node.channel.id})
            </span>
          )}
        </div>

        {/* children column */}
        {isExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {node.children.map(child => (
              <ChannelTreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                expandedPaths={expandedPaths}
                setExpandedPaths={setExpandedPaths}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
    );
}
